<template>
    <div class="content mk-aff-members">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_members") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 12px;">
                        <h3 class="mb-0">{{ $t("marketing.menu.affiliate_members") }}</h3>
                        <div class="d-flex flex-wrap" style="gap: 8px;">
                            <base-button type="primary" size="sm" data-testid="aff-mem-eligibility" @click="eligOpen = true">
                                <i class="fa fa-user-check"></i> {{ $t("marketing.affiliate.eligibility.list_title") }}
                            </base-button>
                            <base-button type="secondary" size="sm" :loading="exporting" :disabled="schemaMissing" data-testid="aff-mem-export" @click="exportCsv">
                                <i class="fa fa-file-csv"></i> {{ $t("marketing.affiliate.export_csv") }}
                            </base-button>
                        </div>
                    </div>
                </template>

                <div v-if="schemaMissing" class="px-4 pb-3">
                    <el-alert type="error" :closable="false" show-icon :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
                </div>

                <div class="px-4 pb-2">
                    <el-radio-group v-model="f.status" size="small" data-testid="aff-mem-status" @change="reload(1)">
                        <el-radio-button v-for="s in statusTabs" :key="s" :label="s">{{ tk("marketing.affiliate.member.tab_", s) }}</el-radio-button>
                    </el-radio-group>
                </div>
                <div class="d-flex flex-wrap align-items-center px-4 pb-3" style="gap: 12px;">
                    <el-input v-model="f.q" size="small" clearable prefix-icon="el-icon-search" :placeholder="$t('marketing.affiliate.member.search')" style="max-width: 260px;" data-testid="aff-mem-search" @input="onSearch"></el-input>
                    <el-select v-model="f.profile_status" size="small" clearable :placeholder="$t('marketing.affiliate.member.docs_all')" style="width: 180px;" data-testid="aff-mem-docs" @change="reload(1)">
                        <el-option v-for="s in profileStatuses" :key="s" :value="s" :label="tk('marketing.affiliate.profile.status_', s)"></el-option>
                    </el-select>
                    <el-checkbox v-model="f.hold" data-testid="aff-mem-hold" @change="reload(1)">{{ $t("marketing.affiliate.member.hold") }}</el-checkbox>
                </div>

                <el-table
                    v-loading="loading"
                    :data="rows"
                    class="aff-table bon-table bon-tight"
                    row-key="member_id"
                    header-row-class-name="thead-light"
                    :empty-text="$t('marketing.affiliate.empty')"
                    row-class-name="mk-click-row"
                    data-testid="aff-mem-table"
                    @row-click="openDetail"
                >
                    <!-- 1440 px + open sidebar (verifier B): fixed short columns,
                         phone under the name, joined date under the code. -->
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.code')" width="118">
                        <template v-slot="{ row }">
                            <span class="aff-mono font-weight-bold">{{ row.code }}</span>
                            <a href="#" class="ml-1 text-muted" :title="$t('marketing.affiliate.copy')" @click.prevent.stop="copy(row.code)"><i class="fa fa-copy"></i></a>
                            <div class="small text-muted" :title="$t('marketing.affiliate.member.joined')">{{ dateOnly(row.joined_at) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.name') + ' / ' + $t('marketing.affiliate.member.phone')" min-width="130">
                        <template v-slot="{ row }">
                            <div class="aff-ellipsis">{{ row.member_deleted ? $t("marketing.affiliate.member.deleted_name") : row.name }}</div>
                            <div class="small text-muted">{{ row.phone_masked || "-" }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.status')" width="124">
                        <template v-slot="{ row }">
                            <el-tag size="small" :type="statusTag(row)">{{ tk("marketing.affiliate.member.status_", statusKey(row)) }}</el-tag>
                            <div v-if="row.hold_payout"><el-tag size="mini" type="danger" class="mt-1">{{ $t("marketing.affiliate.member.hold") }}</el-tag></div>
                            <div v-if="row.affiliate_eligible === false"><el-tag size="mini" type="warning" class="mt-1" data-testid="aff-mem-not-eligible">{{ $t("marketing.affiliate.eligibility.row_not_eligible") }}</el-tag></div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.docs')" width="132">
                        <template v-slot="{ row }"><el-tag size="small" :type="profileTag(row.profile_status)">{{ tk("marketing.affiliate.profile.status_", row.profile_status) }}</el-tag></template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.clicks_30d')" width="76" align="right" prop="clicks_30d"></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.orders_30d')" width="76" align="right" prop="orders_30d"></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.pending')" width="92" align="right"><template v-slot="{ row }">{{ money(row.pending) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.approved')" width="92" align="right"><template v-slot="{ row }">{{ money(row.approved_unpaid) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.in_payout')" width="92" align="right"><template v-slot="{ row }">{{ money(row.in_payout) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.paid_total')" width="100" align="right"><template v-slot="{ row }">{{ money(row.paid_gross_total) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.actions')" width="76" align="center" fixed="right">
                        <template v-slot="{ row }">
                            <div @click.stop>
                                <el-dropdown trigger="click" @command="(c) => onAction(c, row)">
                                    <base-button size="sm" type="neutral" class="px-2" :aria-label="$t('marketing.affiliate.actions')"><i class="fa fa-ellipsis-v"></i></base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="view">{{ $t("marketing.affiliate.member.view") }}</el-dropdown-item>
                                        <template v-if="canWriteMk">
                                            <el-dropdown-item v-if="row.status === 'suspended'" command="unsuspend" divided>{{ $t("marketing.affiliate.member.unsuspend") }}</el-dropdown-item>
                                            <el-dropdown-item v-else command="suspend" divided class="text-danger">{{ $t("marketing.affiliate.member.suspend") }}</el-dropdown-item>
                                        </template>
                                        <template v-if="canWriteFin">
                                            <el-dropdown-item v-if="row.hold_payout" command="hold_off" :divided="!canWriteMk">{{ $t("marketing.affiliate.member.hold_off") }}</el-dropdown-item>
                                            <el-dropdown-item v-else command="hold_on" :divided="!canWriteMk">{{ $t("marketing.affiliate.member.hold_on") }}</el-dropdown-item>
                                        </template>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </div>
                        </template>
                    </el-table-column>
                </el-table>

                <div slot="footer" class="d-flex justify-content-end px-3">
                    <el-pagination layout="total, prev, pager, next" :total="total" :page-size="limit" :current-page="f.page" @current-change="reload"></el-pagination>
                </div>
            </card>
        </div>

        <!-- suspend dialog (§5.3): reason + "also hold payouts" (default on) -->
        <el-dialog :title="$t('marketing.affiliate.member.suspend')" :visible.sync="suspendOpen" width="460px" append-to-body>
            <div data-testid="aff-suspend-dialog">
                <p class="mb-2">{{ suspendRow ? suspendRow.code + " · " + suspendRow.name : "" }}</p>
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.member.suspend_reason") }}</label>
                    <el-input v-model="suspendForm.reason" type="textarea" :rows="3" maxlength="300" show-word-limit data-testid="aff-suspend-reason"></el-input>
                    <small v-if="suspendErr" class="text-danger">{{ $t("marketing.affiliate.reason_required") }}</small>
                </div>
                <el-checkbox v-model="suspendForm.hold_payout" data-testid="aff-suspend-hold">{{ $t("marketing.affiliate.member.suspend_hold") }}</el-checkbox>
            </div>
            <span slot="footer">
                <base-button type="secondary" @click="suspendOpen = false">{{ $t("marketing.affiliate.cancel") }}</base-button>
                <base-button type="danger" :loading="acting" data-testid="aff-suspend-confirm" @click="doSuspend">{{ $t("marketing.affiliate.member.suspend") }}</base-button>
            </span>
        </el-dialog>

        <affiliate-member-detail :visible.sync="detailOpen" :member-id="detailId" @changed="reload()"></affiliate-member-detail>
        <affiliate-eligibility-dialog :visible.sync="eligOpen" @changed="reload()"></affiliate-eligibility-dialog>
    </div>
</template>

<script>
import { Table, TableColumn, Tag, Alert, RadioGroup, RadioButton, Dropdown, DropdownMenu, DropdownItem, Pagination, Select, Option, Input, Checkbox, Dialog, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { isSchemaMissing } from "@/utils/marketing";
import { copyToClipboard } from "@/utils/clipboard";
import { LIST_LIMIT, MEMBER_STATUS_TABS, PROFILE_STATUSES, memberQuery, memberStatusKey, profileStatusTag, saveBlob } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";
import AffiliateMemberDetail from "./components/AffiliateMemberDetail.vue";
import AffiliateEligibilityDialog from "./components/AffiliateEligibilityDialog.vue";

// การตลาด → Affiliate: สมาชิก (Phase 3 spec 2026-10-13 §5.3).
export default {
    name: "MarketingAffiliateMembers",
    directives: { loading: Loading.directive },
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Pagination.name]: Pagination,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        [Checkbox.name]: Checkbox,
        [Dialog.name]: Dialog,
        AffiliateMemberDetail,
        AffiliateEligibilityDialog
    },
    mixins: [affiliateMixin],
    data() {
        return {
            loading: false,
            exporting: false,
            schemaMissing: false,
            rows: [],
            total: 0,
            limit: LIST_LIMIT,
            statusTabs: MEMBER_STATUS_TABS,
            profileStatuses: PROFILE_STATUSES,
            f: { page: 1, status: "all", q: "", profile_status: "", hold: false },
            searchTimer: null,
            loadedOnce: false,
            detailOpen: false,
            eligOpen: false,
            detailId: null,
            suspendOpen: false,
            suspendRow: null,
            suspendForm: { reason: "", hold_payout: true },
            suspendErr: false,
            acting: false
        };
    },
    computed: {
        ...mapGetters({ canWriteMk: "Auth/canWriteMarketing", canWriteFin: "Auth/canWriteAffiliateFinance" })
    },
    created() {
        if (this.$route.query.profile_status) this.f.profile_status = String(this.$route.query.profile_status);
        this.reload(1);
    },
    activated() {
        if (this.loadedOnce) this.reload();
    },
    methods: {
        statusKey: memberStatusKey,
        profileTag: profileStatusTag,
        statusTag(row) {
            const k = memberStatusKey(row);
            return k === "active" ? "success" : k === "suspended" ? "danger" : "info";
        },
        async reload(page) {
            if (page) this.f.page = page;
            this.loading = true;
            try {
                const res = await AffiliateService.affiliates(memberQuery(this.f, this.limit));
                const body = res.data || {};
                this.rows = body.data || [];
                this.total = body.total || 0;
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                this.total = 0;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
                this.loadedOnce = true;
            }
        },
        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(1), 350);
        },
        async copy(text) {
            const ok = await copyToClipboard(text);
            this.$notify({ type: ok ? "success" : "danger", message: ok ? this.$t("marketing.affiliate.copied") : this.$t("marketing.affiliate.err.generic") });
        },
        async exportCsv() {
            this.exporting = true;
            try {
                const params = memberQuery(this.f, this.limit);
                delete params.page;
                delete params.limit;
                saveBlob(await AffiliateService.exportAffiliatesCsv(params), "affiliates.csv");
            } catch (err) {
                this.showError(err);
            } finally {
                this.exporting = false;
            }
        },
        openDetail(row) {
            this.detailId = row.member_id;
            this.detailOpen = true;
        },
        async onAction(cmd, row) {
            if (cmd === "view") return this.openDetail(row);
            if (cmd === "suspend") {
                this.suspendRow = row;
                this.suspendForm = { reason: "", hold_payout: true };
                this.suspendErr = false;
                this.suspendOpen = true;
                return;
            }
            try {
                if (cmd === "unsuspend") {
                    if (!(await this.confirmBox(this.$t("marketing.affiliate.member.confirm_unsuspend", { code: row.code })))) return;
                    await AffiliateService.unsuspend(row.member_id);
                } else if (cmd === "hold_on" || cmd === "hold_off") {
                    const hold = cmd === "hold_on";
                    const reason = await this.askReason(this.$t(hold ? "marketing.affiliate.member.hold_on" : "marketing.affiliate.member.hold_off"), { text: row.code });
                    if (reason === null) return;
                    await AffiliateService.hold(row.member_id, { hold, reason });
                } else return;
                this.notifyOk();
                this.reload();
            } catch (err) {
                this.showError(err);
            }
        },
        async doSuspend() {
            const reason = String(this.suspendForm.reason || "").trim();
            this.suspendErr = !reason;
            if (!reason || !this.suspendRow) return;
            this.acting = true;
            try {
                await AffiliateService.suspend(this.suspendRow.member_id, { reason, hold_payout: !!this.suspendForm.hold_payout });
                this.suspendOpen = false;
                this.notifyOk();
                this.reload();
            } catch (err) {
                this.showError(err);
            } finally {
                this.acting = false;
            }
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-mono {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: 0.06em;
}
.aff-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.mk-aff-members >>> .mk-click-row {
    cursor: pointer;
}
</style>
